import { PureComponent } from 'react'
// import PropTypes from 'prop-types'
import { Row, Col } from 'antd'
import _ from 'lodash'
import classNames from 'classnames'
import { ResponsiveContainer, Surface, Symbols, BarChart, Bar, XAxis, YAxis, Text } from 'recharts'
import styles from './BrandChart.css'
import { BaseStatHelper } from '../Shared'
import { YCardTitle } from '../../../Common/YCard'
import { BRAND_STAT_KEY_MAP, BRAND_STAT_KEYS } from '../../../../constants/globalTypes'
import { numeralFormat } from '../../../../../lib/renderUtil'

// const COLORS = ['#ffc658', '#d0ed57', '#a4de6c', '#8884d8', '#8dd1e1', '#82ca9d', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#83a6ed']

const ICON_SIZE = 10
const SIZE = 32

const AGE_KEYS = [
  '0~19',
  '20~29',
  '30~39',
  '40~49',
  '50+'
]

const GENDER_KEY_MAP = {
  M: '男',
  F: '女'
}

const GENDER_KEYS = _.keys(GENDER_KEY_MAP)

const CustomizedAxisTick = ({ x, y, stroke, payload }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor='middle'
        fill='#828282'
        className={styles.axisLabel}
      >
        {payload.value}
      </text>
    </g>
  )
}

const convertRatioToPercentage = (ratio) => {
  return ((ratio) * 100).toFixed(0)
}

class BrandHelper extends BaseStatHelper {
  convertBrandStatValue (key, value) {
    switch (key) {
      case 'orderYoy':
      case 'salesYoy':
        return numeralFormat(value || null, { format: '0%', allowZero: true })
      case 'purchaseFrequency': {
        // 加入設定檔
        const formatedValue = numeralFormat(value, { format: '0.00', allowZero: true })
        return formatedValue
      }
      case 'averageOrderValue': {
        // 加入設定檔
        const formatedValue = numeralFormat(value, { allowZero: true })
        return formatedValue
      }
      default:
        return numeralFormat(value, { format: '0', allowZero: true })
    }
  }
}

const Helper = new BrandHelper(BRAND_STAT_KEY_MAP)

class CustomizedLegend extends PureComponent {
  renderIcon = (data) => {
    const halfSize = SIZE / 2
    const color = data.color

    return (
      <Symbols
        fill={color}
        cx={halfSize}
        cy={halfSize}
        size={SIZE}
        sizeType='diameter'
        type='square'
      />
    )
  }

  renderLegendItem = (item) => {
    const viewBox = { x: 0, y: 0, width: SIZE, height: SIZE }
    const itemStyle = {
      display: 'inline-block'
    }
    const svgStyle = { display: 'inline-block', marginRight: 4 }

    const className = classNames(styles.legendItem)

    // generate svg icon before the text
    return (
      <span
        className={className}
        style={itemStyle}
      >
        <Surface
          width={ICON_SIZE}
          height={ICON_SIZE}
          viewBox={viewBox}
          style={svgStyle}
        >
          {this.renderIcon(item)}
        </Surface>
        <span className={styles.legendItemText}>{item.name}</span>
      </span>
    )
  }

  renderLegend = () => {
    const { data } = this.props
    const renderItems = _.map(data, (item) => {
      return this.renderLegendItem(item)
    })
    return (
      <div className={styles.legend}>{renderItems}</div>
    )
  }

  render () {
    return this.renderLegend()
  }
}

const BrandStatCardList = ({ brandStat, className }) => {
  // const data = brandStat
  if (!brandStat) {
    return null
  }
  // const preloadedData = data
  const listItems = !_.isEmpty(brandStat)
    ? _.map(
      brandStat,
      (value, key) => {
        const content = Helper.convertBrandStatValue(key, value)
        const title = Helper.getTooltip(key)
        return (
          <Col
            key={key}
            span={12}
          >
            <div className={styles.brandStatCard}>
              <YCardTitle
                title={title}
                content={Helper.getText(key)}
                className={styles.brandStatCardTitle}
              />
              <div className={styles.brandStatCardItem}>
                {content}
              </div>
            </div>
          </Col>
        )
      })
    : null

  return (
    <Row
      data-role='brandStat-list'
      gutter={[12, 12]}
    >
      {listItems}
    </Row>
  )
}

const BRAND_CHART_LEGEND_DATA = [{ name: '性別', color: '#893BC5' }, { name: '年齡', color: '#F6BBA3' }]

const getChartData = (nextProps, prevState) => {
  if (!_.isEqual(prevState.data, nextProps.data)) {
    const localData = nextProps.data

    const rawBrandStat = _.get(localData, 'yoy', {})
    const brandStat = _.reduce(BRAND_STAT_KEYS, (resultObj, yoyKey) => {
      const yoyKeyFound = _.get(rawBrandStat, yoyKey, null)
      resultObj[yoyKey] = yoyKeyFound || null
      return resultObj
    }, {})
    const demographics = _.get(localData, 'demographics', [])
    const rawGenderData = _.get(_.find(demographics, { report: 'buyersGender' }), 'distributions', [])
    const rawAgeData = _.get(_.find(demographics, { report: 'buyersAge' }), 'distributions', [])

    const genderData = _.map(GENDER_KEYS, (key, idx) => {
      let distribution = _.find(rawGenderData, { key })
      if (_.isEmpty(distribution)) {
        distribution = {
          key,
          ratio: '0'
        }
      }
      return {
        ...distribution,
        name: _.get(GENDER_KEY_MAP, distribution.key, ''),
        percentage: convertRatioToPercentage(distribution.ratio)
      }
    })
    const ageData = _.map(AGE_KEYS, (key, idx) => {
      let distribution = _.find(rawAgeData, { key })
      if (_.isEmpty(distribution)) {
        distribution = {
          key,
          ratio: '0'
        }
      }
      return {
        ...distribution,
        name: distribution.key,
        percentage: convertRatioToPercentage(distribution.ratio)
      }
    })
    return {
      brandStat,
      genderData,
      ageData
    }
  }
  return null
}

class BrandChart extends PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      width: 945,
      data: {}
    }
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    const data = getChartData(nextProps, prevState)
    if (data) {
      return { data }
    }
    return null
  }

  renderCustomLabel = (props) => {
    const { x, y, value } = props
    return (
      <Text
        x={x}
        y={y}
        dy={-10}
        fill='#828282'
        className={styles.labelText}
      >
        {`${value}%`}
      </Text>
    )
  }

  render () {
    const { data } = this.state
    const {
      brandStat,
      genderData,
      ageData
    } = data
    const clz = classNames(styles.brandChart, {
      // [className]: !!className
    })

    return (
      <div className={clz}>
        <div className={styles.divLeft}>
          <section className={styles.chartCard}>
            <YCardTitle
              title={'此品牌之買家性別、年齡分佈'}
              className={styles.chartCardTitle}
            >
              買家輪廓分佈
            </YCardTitle>
            <div className={styles.chartContainer}>
              <ResponsiveContainer
                width='25%'
                height='100%'
                minHeight={225}
              >
                <BarChart
                  data={genderData}
                  barSize={30}
                  margin={{ top: 25, right: 0, left: 0, bottom: 5 }}
                >
                  <XAxis
                    dataKey='name'
                    tickLine={false}
                    tick={<CustomizedAxisTick />}
                    axisLine={{ stroke: '#E2E2E2' }}
                  />
                  <YAxis
                    hide
                    domain={[0, 100]}
                  />
                  <Bar
                    dataKey='percentage'
                    fill='#893BC5'
                    label={this.renderCustomLabel}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
              <ResponsiveContainer
                width='75%'
                height='100%'
                minHeight={225}
              >
                <BarChart
                  data={ageData}
                  barSize={30}
                  margin={{ top: 25, right: 0, left: 0, bottom: 5 }}
                  isAnimationActiveBoolean={false}
                >
                  <XAxis
                    dataKey='name'
                    tickLine={false}
                    tick={<CustomizedAxisTick />}
                    axisLine={{ stroke: '#E2E2E2' }}
                  />
                  <YAxis
                    hide
                    domain={[0, 100]}
                  />
                  {/*
                    set isAnimationActive={false} when dynamic rendering chart
                    https://github.com/recharts/recharts/issues/829
                  */}
                  <Bar
                    dataKey='percentage'
                    fill='#F6BBA3'
                    label={this.renderCustomLabel}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <CustomizedLegend data={BRAND_CHART_LEGEND_DATA} />
          </section>
        </div>
        <div className={styles.divRight}>
          <BrandStatCardList brandStat={brandStat} />
        </div>
      </div>
    )
  }
}

export default BrandChart
